/**
 * Super Admin → Configuration health.
 *
 * Lists alerts about missing / placeholder configuration (e.g. a concentration
 * account still holding 'PENDING' banking details) so an operator can fix them
 * before a client or tester hits the runtime error. Read-only: it reports, it
 * does not change anything.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import configHealthService, {
  type AlertSeverity,
  type ConfigAlert,
  type ConfigHealth,
} from '../../services/configHealth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';

const SEV_ORDER: Record<AlertSeverity, number> = { high: 0, medium: 1, low: 2 };

const SEV_STYLE: Record<AlertSeverity, { bg: string; border: string; text: string; label: string }> = {
  high: { bg: colors.dangerBg, border: colors.dangerBorder, text: colors.danger, label: 'High' },
  medium: { bg: colors.warningBg, border: '#FCD34D', text: colors.warning, label: 'Medium' },
  low: { bg: colors.infoBg, border: colors.infoText, text: colors.infoText, label: 'Low' },
};

function AlertCard({ alert }: { readonly alert: ConfigAlert }): JSX.Element {
  const s = SEV_STYLE[alert.severity];
  return (
    <div data-alert={alert.code} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: '14px 16px', display: 'flex', gap: 12 }}>
      <span style={{ flexShrink: 0, alignSelf: 'flex-start', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: s.text, background: s.bg, border: `1px solid ${s.border}`, borderRadius: 6, padding: '2px 8px' }}>
        {s.label}
      </span>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
          {alert.area}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: colors.ink, marginBottom: 4 }}>{alert.title}</div>
        <div style={{ fontSize: 13, color: colors.inkSoft }}>{alert.detail}</div>
        {alert.fixed_in && (
          <div style={{ fontSize: 13, color: colors.inkSoft, marginTop: 6 }}>
            <span style={{ fontWeight: 600 }}>Where to fix it:</span>{' '}
            {alert.fix_path ? (
              <Link to={alert.fix_path} style={{ color: colors.navy, fontWeight: 600, textDecoration: 'underline' }}>{alert.fixed_in}</Link>
            ) : (
              alert.fixed_in
            )}
          </div>
        )}
        {alert.needs_second_person && (
          <div style={{ fontSize: 13, color: colors.inkSoft, marginTop: 2 }}>A second person must confirm it.</div>
        )}
      </div>
    </div>
  );
}

export default function ConfigHealthPage(): JSX.Element {
  const [data, setData] = useState<ConfigHealth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((): void => {
    setLoading(true);
    setError(null);
    configHealthService.get()
      .then(setData)
      .catch((e: unknown) => setError(getApiErrorMessage(e, 'Could not load configuration health.')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const alerts = [...(data?.alerts ?? [])].sort((a, b) => SEV_ORDER[a.severity] - SEV_ORDER[b.severity]);

  return (
    <div style={{ maxWidth: 880, margin: '0 auto' }}>
      <PageHeader
        title="Configuration health"
        subtitle="Missing or placeholder configuration that would otherwise surface as a runtime error."
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 13, color: colors.textMuted }}>
          {data ? `${data.checks_run} check(s) · ${alerts.length} alert(s)` : ''}
        </span>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.surface, fontSize: 13, fontWeight: 600, color: colors.ink, cursor: loading ? 'wait' : 'pointer' }}
        >
          {loading ? 'Checking…' : 'Re-check'}
        </button>
      </div>

      {error && (
        <div style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, borderRadius: 8, padding: '10px 14px', color: colors.danger, fontSize: 13 }}>
          {error}
        </div>
      )}

      {!error && !loading && alerts.length === 0 && (
        <div style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, borderRadius: 12, padding: '16px 18px', color: colors.successText, fontSize: 14 }}>
          ✓ All configuration checks passed — no missing or placeholder configuration found.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {alerts.map((a) => <AlertCard key={`${a.code}:${a.title}`} alert={a} />)}
      </div>
    </div>
  );
}
